import { useEffect, useMemo, useRef, useState } from 'react';

const parseIceServerList = (value) => {
  if (!value) return [];
  return String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((url) => ({ urls: url }));
};

const stunServers = parseIceServerList(
  process.env.REACT_APP_VOIP_STUN_URLS ||
  'stun:stun.l.google.com:19302,stun:stun1.l.google.com:19302'
);

const turnUrls = String(process.env.REACT_APP_VOIP_TURN_URLS || '')
  .split(',')
  .map((item) => item.trim())
  .filter(Boolean);
const turnUsername = process.env.REACT_APP_VOIP_TURN_USERNAME || '';
const turnCredential = process.env.REACT_APP_VOIP_TURN_CREDENTIAL || '';

const turnServers = (turnUrls.length > 0 && turnUsername && turnCredential)
  ? [{ urls: turnUrls, username: turnUsername, credential: turnCredential }]
  : [
    {
      urls: [
        'stun:openrelay.metered.ca:80',
        'turn:openrelay.metered.ca:80'
      ],
      username: 'openrelayproject',
      credential: 'openrelayproject'
    }
  ];

const RTC_CONFIG = {
  iceServers: [...stunServers, ...turnServers]
};

const isVoipDebugEnabled = () => {
  if (process.env.REACT_APP_VOIP_DEBUG === '1') return true;
  if (typeof window !== 'undefined') {
    try {
      return window.localStorage.getItem('voip_debug') === '1';
    } catch {
      return false;
    }
  }
  return false;
};

const voipLog = (...args) => {
  if (!isVoipDebugEnabled()) return;
  // Keep logs lightweight and easy to filter.
  console.info('[voip]', ...args);
};

export default function useVoipMesh({
  ws,
  user,
  enabled,
  callId,
  scope,
  targetId,
  peerIds = []
}) {
  const [remoteStreams, setRemoteStreams] = useState({});
  const localStreamRef = useRef(null);
  const peerConnectionsRef = useRef({});
  const pendingIceCandidatesRef = useRef({});

  const normalizedPeers = useMemo(
    () => [...new Set((peerIds || []).filter((id) => id && id !== user?.id))],
    [peerIds, user?.id]
  );

  const sendWs = (payload) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(payload));
  };

  const ensureLocalStream = async () => {
    if (localStreamRef.current) return localStreamRef.current;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    localStreamRef.current = stream;
    return stream;
  };

  const createPeer = async (peerId) => {
    if (!enabled || !callId || !peerId || peerConnectionsRef.current[peerId]) return peerConnectionsRef.current[peerId];
    const stream = await ensureLocalStream();
    const pc = new RTCPeerConnection(RTC_CONFIG);
    voipLog('peer:create', { callId, scope, targetId, selfId: user?.id, peerId });

    stream.getTracks().forEach((track) => pc.addTrack(track, stream));

    pc.onicecandidate = (event) => {
      if (!event.candidate) return;
      voipLog('signal:send:ice', { callId, to: peerId, candidate: event.candidate?.candidate || '' });
      sendWs({
        type: 'voip_signal',
        call_id: callId,
        scope,
        target_id: targetId,
        recipient_id: peerId,
        signal_type: 'ice',
        candidate: event.candidate
      });
    };

    pc.ontrack = (event) => {
      const [streamValue] = event.streams;
      if (!streamValue) return;
      voipLog('track:remote', { callId, from: peerId, trackCount: streamValue.getTracks().length });
      setRemoteStreams((prev) => ({ ...prev, [peerId]: streamValue }));
    };

    pc.oniceconnectionstatechange = () => {
      voipLog('pc:iceConnectionState', { callId, peerId, state: pc.iceConnectionState });
    };

    pc.onicegatheringstatechange = () => {
      voipLog('pc:iceGatheringState', { callId, peerId, state: pc.iceGatheringState });
    };

    pc.onsignalingstatechange = () => {
      voipLog('pc:signalingState', { callId, peerId, state: pc.signalingState });
    };

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      voipLog('pc:connectionState', { callId, peerId, state });
      if (state === 'disconnected' || state === 'failed' || state === 'closed') {
        setRemoteStreams((prev) => {
          const next = { ...prev };
          delete next[peerId];
          return next;
        });
      }
    };

    peerConnectionsRef.current[peerId] = pc;
    return pc;
  };

  const createOfferForPeer = async (peerId) => {
    const pc = await createPeer(peerId);
    if (!pc) return;
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    voipLog('signal:send:offer', { callId, to: peerId, type: offer.type });
    sendWs({
      type: 'voip_signal',
      call_id: callId,
      scope,
      target_id: targetId,
      recipient_id: peerId,
      signal_type: 'offer',
      sdp: offer
    });
  };

  const closeAll = () => {
    Object.values(peerConnectionsRef.current).forEach((pc) => {
      try { pc.close(); } catch {}
    });
    peerConnectionsRef.current = {};
    pendingIceCandidatesRef.current = {};
    setRemoteStreams({});
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        try { track.stop(); } catch {}
      });
      localStreamRef.current = null;
    }
  };

  useEffect(() => {
    if (!enabled || !callId || !user?.id) {
      closeAll();
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await ensureLocalStream();
        // Deterministic offerer to avoid glare: smaller ID starts.
        for (const peerId of normalizedPeers) {
          if (cancelled) return;
          if (String(user.id) < String(peerId)) {
            await createOfferForPeer(peerId);
          } else {
            await createPeer(peerId);
          }
        }
      } catch {
        // Permission denied or device error is handled by caller UI.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, callId, user?.id, normalizedPeers.join('|')]);

  useEffect(() => {
    if (!enabled || !callId || !user?.id || normalizedPeers.length === 0) return;
    const retryId = setInterval(() => {
      normalizedPeers.forEach(async (peerId) => {
        const pc = peerConnectionsRef.current[peerId];
        const state = pc?.connectionState;
        const signaling = pc?.signalingState;
        const hasRemote = Boolean(remoteStreams[peerId]);
        if (hasRemote) return;
        if (state === 'connected') return;
        if (pc && signaling && signaling !== 'stable') return;
        if (String(user.id) <= String(peerId)) {
          try {
            await createOfferForPeer(peerId);
          } catch {
            // Retry loop keeps attempting on transient failures.
          }
        }
      });
    }, 2200);
    return () => clearInterval(retryId);
  }, [enabled, callId, user?.id, normalizedPeers.join('|'), Object.keys(remoteStreams).join('|')]);

  useEffect(() => {
    if (!ws || !enabled || !callId || !user?.id) return;

    const onMessage = async (event) => {
      const data = JSON.parse(event.data);
      if (data.type !== 'voip_signal') return;
      if (data.call_id !== callId) return;
      if (data.recipient_id !== user.id) return;
      const senderId = data.sender_id;
      if (!senderId) return;
      voipLog('signal:recv', {
        callId,
        from: senderId,
        signalType: data.signal_type,
        hasSdp: Boolean(data.sdp),
        hasCandidate: Boolean(data.candidate)
      });

      const pc = await createPeer(senderId);
      if (!pc) return;

      if (data.signal_type === 'offer' && data.sdp) {
        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
        voipLog('signal:apply:offer', { callId, from: senderId });
        const pending = pendingIceCandidatesRef.current[senderId] || [];
        for (const candidate of pending) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          } catch {}
        }
        pendingIceCandidatesRef.current[senderId] = [];
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        voipLog('signal:send:answer', { callId, to: senderId, type: answer.type });
        sendWs({
          type: 'voip_signal',
          call_id: callId,
          scope,
          target_id: targetId,
          recipient_id: senderId,
          signal_type: 'answer',
          sdp: answer
        });
      } else if (data.signal_type === 'answer' && data.sdp) {
        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
        voipLog('signal:apply:answer', { callId, from: senderId });
        const pending = pendingIceCandidatesRef.current[senderId] || [];
        for (const candidate of pending) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          } catch {}
        }
        pendingIceCandidatesRef.current[senderId] = [];
      } else if (data.signal_type === 'ice' && data.candidate) {
        if (!pc.remoteDescription) {
          if (!pendingIceCandidatesRef.current[senderId]) {
            pendingIceCandidatesRef.current[senderId] = [];
          }
          pendingIceCandidatesRef.current[senderId].push(data.candidate);
          voipLog('signal:queue:ice', { callId, from: senderId, queued: pendingIceCandidatesRef.current[senderId].length });
          return;
        }
        try {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
          voipLog('signal:apply:ice', { callId, from: senderId });
        } catch {
          // Ignore stale ICE candidates.
        }
      }
    };

    ws.addEventListener('message', onMessage);
    return () => ws.removeEventListener('message', onMessage);
  }, [ws, enabled, callId, user?.id, scope, targetId]);

  const setMuted = (muted) => {
    if (!localStreamRef.current) return;
    localStreamRef.current.getAudioTracks().forEach((track) => {
      track.enabled = !muted;
    });
  };

  return {
    remoteStreams,
    setMuted,
    closeAll
  };
}
